/**
 * GrantAccessModal - grant a new investor access to a deal room.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X, UserPlus } from 'lucide-react';
import toast from 'react-hot-toast';
import investorService from '../services/investorService';
import { Investor } from '../types/investor';
import { AccessTier } from '../types/dealRoom';

interface GrantAccessModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (payload: {
    investor_id: number;
    access_tier: AccessTier;
    nda_required: boolean;
  }) => Promise<void> | void;
}

const TIER_OPTIONS: Array<{ value: AccessTier; label: string; desc: string }> = [
  { value: 'basic', label: 'Basic', desc: 'Pitch deck + summary' },
  { value: 'qualified', label: 'Qualified', desc: 'Financials + cap table' },
  { value: 'lead', label: 'Lead', desc: 'Full due diligence room' },
];

const GrantAccessModal: React.FC<GrantAccessModalProps> = ({
  open,
  onClose,
  onConfirm,
}) => {
  const [investors, setInvestors] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [tier, setTier] = useState<AccessTier>('basic');
  const [ndaRequired, setNdaRequired] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    const run = async () => {
      setLoading(true);
      const res = await investorService.fetchInvestors({ size: 200 });
      setLoading(false);
      if (res.data) setInvestors(res.data.investors);
    };
    run();
  }, [open]);

  useEffect(() => {
    if (!open) {
      setSearch('');
      setSelectedId(null);
      setTier('basic');
      setNdaRequired(true);
    }
  }, [open]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return investors;
    return investors.filter(
      (i) =>
        i.full_name.toLowerCase().includes(q) ||
        (i.firm_name || '').toLowerCase().includes(q)
    );
  }, [search, investors]);

  const submit = async () => {
    if (!selectedId) {
      toast.error('Select an investor');
      return;
    }
    setSubmitting(true);
    await onConfirm({
      investor_id: selectedId,
      access_tier: tier,
      nda_required: ndaRequired,
    });
    setSubmitting(false);
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={() => !submitting && onClose()}
    >
      <div
        className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Grant Access</h2>
            <p className="text-xs text-gray-600 mt-1">
              Give an investor access to this deal room.
            </p>
          </div>
          <button
            onClick={onClose}
            disabled={submitting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-1">
            Investor
          </label>
          <div className="relative mb-2">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search investors..."
              className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
          <div className="border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-6 text-gray-500 text-sm">
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Loading investors...
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-6 text-center text-gray-400 text-sm">
                No investors found.
              </div>
            ) : (
              filtered.map((inv) => (
                <button
                  key={inv.id}
                  type="button"
                  onClick={() => setSelectedId(inv.id)}
                  className={`w-full text-left px-3 py-2 border-b border-gray-100 last:border-b-0 text-sm hover:bg-gray-50 ${
                    selectedId === inv.id ? 'bg-blue-50' : ''
                  }`}
                >
                  <div className="font-medium text-gray-900">{inv.full_name}</div>
                  {inv.firm_name && (
                    <div className="text-xs text-gray-500">{inv.firm_name}</div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-wide text-gray-500 mb-2">
            Access tier
          </label>
          <div className="grid grid-cols-1 gap-2">
            {TIER_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start gap-2 border rounded-lg p-3 cursor-pointer ${
                  tier === opt.value
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:bg-gray-50'
                }`}
              >
                <input
                  type="radio"
                  name="tier"
                  value={opt.value}
                  checked={tier === opt.value}
                  onChange={() => setTier(opt.value)}
                  className="mt-0.5"
                />
                <div>
                  <div className="text-sm font-medium text-gray-900">
                    {opt.label}
                  </div>
                  <div className="text-xs text-gray-500">{opt.desc}</div>
                </div>
              </label>
            ))}
          </div>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={ndaRequired}
            onChange={(e) => setNdaRequired(e.target.checked)}
          />
          Require NDA before access
        </label>

        <div className="flex justify-end gap-2 pt-2">
          <button
            onClick={onClose}
            disabled={submitting}
            className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={submitting || !selectedId}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4 mr-2" />
            )}
            Grant access
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrantAccessModal;
